import { useLayoutEffect, useRef } from 'react'
import { Ban, GripVertical, RotateCcw, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { emptyChoice, type ChoiceDraft, type VariantKind } from '@/lib/variantShape'

/**
 * The choices grid (F-MOD-N12): rows an owner types into.
 *
 * Tab on the last name adds a row. Up/down arrow keys on the handle reorder.
 * The sold-out button strikes a row through rather than removing it, because
 * the till shows sold-out choices struck through too, and an owner who
 * deletes "Oat" in a hurry loses its recipe link; one who marks it sold out
 * does not.
 */
export function ChoicesGrid({
  kind,
  choices,
  onChange,
  currencyPrefix,
  disabled,
}: {
  kind: VariantKind
  choices: ChoiceDraft[]
  onChange: (next: ChoiceDraft[]) => void
  currencyPrefix: string
  disabled?: boolean
}) {
  const nameRefs = useRef<Array<HTMLInputElement | null>>([])
  // Row to focus once it exists. Set by add()/move(), consumed right after
  // commit so a fast typist's next keystroke lands in the new row, not the
  // old one (a setTimeout lost that race).
  const focusIndex = useRef<number | null>(null)
  useLayoutEffect(() => {
    if (focusIndex.current == null) return
    nameRefs.current[focusIndex.current]?.focus()
    focusIndex.current = null
  })
  const isSize = kind === 'size'

  const update = (i: number, patch: Partial<ChoiceDraft>) =>
    onChange(choices.map((c, j) => (j === i ? { ...c, ...patch } : c)))

  const setDefault = (i: number, on: boolean) => {
    if (kind === 'many') return update(i, { isDefault: on })
    // Single-pick: at most one default.
    onChange(choices.map((c, j) => ({ ...c, isDefault: j === i ? on : false })))
  }

  const add = () => {
    focusIndex.current = choices.length
    onChange([...choices, emptyChoice()])
  }

  const remove = (i: number) => onChange(choices.filter((_, j) => j !== i))

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir
    if (j < 0 || j >= choices.length) return
    const next = [...choices]
    ;[next[i], next[j]] = [next[j], next[i]]
    focusIndex.current = j
    onChange(next)
  }

  return (
    <div className="overflow-hidden rounded-lg border">
      <div className="grid grid-cols-[20px_minmax(0,1fr)_6.5rem_3rem_1.75rem_1.75rem] items-center gap-2 border-b bg-muted/60 px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <span />
        <span>Choice</span>
        <span className="text-right">{isSize ? 'Item price' : 'Extra charge'}</span>
        <span className="text-center">{kind === 'many' ? 'Pre-tick' : 'Default'}</span>
        <span />
        <span />
      </div>

      {choices.map((c, i) => (
        <div
          key={i}
          className={cn(
            'grid grid-cols-[20px_minmax(0,1fr)_6.5rem_3rem_1.75rem_1.75rem] items-center gap-2 border-b px-2 py-1 last:border-b-0',
            !c.isAvailable && 'bg-muted/30',
          )}
        >
          <button
            type="button"
            className="grid h-7 place-items-center text-muted-foreground/50 hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Reorder ${c.name || 'choice'} with the arrow keys`}
            title="Arrow keys move this row"
            disabled={disabled}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp') { e.preventDefault(); move(i, -1) }
              if (e.key === 'ArrowDown') { e.preventDefault(); move(i, 1) }
            }}
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>

          <input
            ref={(el) => { nameRefs.current[i] = el }}
            type="text"
            value={c.name}
            disabled={disabled}
            placeholder={isSize ? (i === 0 ? 'Regular' : 'Large') : (i === 0 ? 'Full cream' : 'Oat')}
            aria-label={`Choice ${i + 1} name`}
            className={cn(
              'h-8 min-w-0 rounded-md border border-transparent bg-transparent px-2 text-sm hover:border-input focus:border-primary focus:bg-background focus:outline-none',
              !c.isAvailable && 'text-muted-foreground line-through',
            )}
            onChange={(e) => update(i, { name: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Tab' && !e.shiftKey && i === choices.length - 1 && c.name.trim() !== '') {
                e.preventDefault()
                add()
              }
              if (e.key === 'Enter') {
                e.preventDefault()
                if (i === choices.length - 1) add()
                else nameRefs.current[i + 1]?.focus()
              }
            }}
          />

          <span className="flex items-center justify-end gap-1">
            <span className="text-xs tabular-nums text-muted-foreground">{isSize ? currencyPrefix : `+${currencyPrefix}`}</span>
            <input
              type="number"
              min={0}
              step="any"
              inputMode="decimal"
              disabled={disabled}
              value={Number.isFinite(c.price) && c.price !== 0 ? c.price : c.price === 0 && isSize ? 0 : (c.price || '')}
              placeholder={isSize ? '0' : '0'}
              aria-label={`${c.name || 'Choice'} ${isSize ? 'price' : 'extra charge'}`}
              className="h-8 w-20 rounded-md border border-transparent bg-transparent px-2 text-right text-sm tabular-nums hover:border-input focus:border-primary focus:bg-background focus:outline-none"
              onChange={(e) => update(i, { price: e.target.value === '' ? 0 : Number(e.target.value) })}
            />
          </span>

          <span className="flex justify-center">
            <Checkbox
              checked={c.isDefault}
              disabled={disabled}
              onCheckedChange={(on) => setDefault(i, Boolean(on))}
              aria-label={`${c.name || 'Choice'} pre-selected`}
            />
          </span>

          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            disabled={disabled}
            title={c.isAvailable ? 'Mark sold out' : 'Back in stock'}
            aria-label={c.isAvailable ? `Mark ${c.name || 'choice'} sold out` : `Put ${c.name || 'choice'} back in stock`}
            onClick={() => update(i, { isAvailable: !c.isAvailable })}
          >
            {c.isAvailable ? <Ban className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}
          </Button>

          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-muted-foreground"
            disabled={disabled}
            aria-label={`Remove ${c.name || 'choice'}`}
            onClick={() => remove(i)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}

      <div className="flex flex-wrap items-center gap-2 border-t bg-muted/30 px-2 py-1.5 text-xs text-muted-foreground">
        <Button type="button" size="sm" variant="outline" className="h-7" disabled={disabled} onClick={add}>
          + Add choice
        </Button>
        <span>
          or press <kbd className="rounded border px-1 font-mono text-[10px]">Tab</kbd> on the last row.
          {' '}<Ban className="inline h-3 w-3" aria-hidden /> marks a choice sold out; it stays on the till, struck through.
        </span>
      </div>
    </div>
  )
}
